import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Check, ChevronsUpDown, Loader2, ExternalLink } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command'
import { Badge } from '@/components/ui/badge'
import { resolveServerIcon } from './server-icons'
import { IS_BOOSTER_ONLY } from '../product'

/** Рабочая сводка, которую сервер шлёт о себе раз в минуту. */
interface ServerRuntime {
  product?: string
  projects_running?: number
  projects_total?: number
  workers_alive?: number
  workers_active?: number
  threads_cfg?: number
  ops_hour?: number        // -1 = темп ещё не замерен
  fail_pct?: number
  captcha_pct?: number
  load1?: number
  cores?: number
  kind?: string            // galaxy | space | pusher | mixed
  idents?: string[]        // пулы и имена проектов — для поиска
  ts?: number
}

interface ServerInfo {
  id?: number
  subdomain: string
  domain: string
  url: string
  is_online: boolean
  suspended: boolean
  install_status: string
  server_name?: string
  server_icon?: string
  ip?: string
  cpu_percent?: number
  memory_percent?: number
  engine_version?: string
  last_heartbeat?: number
  runtime?: ServerRuntime
  runtime_ts?: number
}

interface MyServersResponse {
  servers?: ServerInfo[]
  panel_username?: string
}

interface ServerSwitcherProps {
  collapsed: boolean
  initialServers?: ServerInfo[]
}

/**
 * Sidebar-top dropdown that lists all servers belonging to the same
 * license as the current one and lets the user jump between them in
 * a single click. The current server is highlighted with a check mark
 * and stays at the top of the list.
 *
 * Hidden entirely when the license has only one server (or when we
 * fail to fetch the list — e.g. dev box without a real license key).
 */
// Поиск, вкладки Galaxy/Space и строка состояния — только в профайлер-сборке
// (и в полной — это наша панель). В booster-сборке переключатель остаётся
// ровно таким, каким был: имя сервера и подпись оператора.
const RICH_SWITCHER = !IS_BOOSTER_ONLY

export default function ServerSwitcher({ collapsed, initialServers }: ServerSwitcherProps) {
  const [servers, setServers] = useState<ServerInfo[]>(initialServers || [])
  const [loading, setLoading] = useState(!initialServers)
  const [open, setOpen] = useState(false)
  // Вкладка по типу пула профилей. Показываем её только когда в списке
  // реально есть серверы обоих типов — иначе это лишний элемент.
  const [tab, setTab] = useState<'all' | 'galaxy' | 'space'>('all')
  // Trigger ref + measured width — used to make the popover match the
  // sidebar button. Without this the popover is fixed at 320px and
  // overhangs/underhangs the sidebar at non-default sidebar widths.
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [triggerWidth, setTriggerWidth] = useState<number>(0)
  useEffect(() => {
    const el = triggerRef.current
    if (!el) return
    const apply = () => setTriggerWidth(el.getBoundingClientRect().width)
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Resolved at component mount: matches the panel we are currently
  // looking at. Backend doesn't tell us this, but the URL does — and
  // it's identical to the `domain` field in the my-servers payload.
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : ''

  useEffect(() => {
    // Если данные пришли через props (из dashboard-init) — начальный fetch не нужен.
    // Polling оставляем только для обновления онлайн-статуса серверов (каждые 120с).
    // /api/my-servers дублировался с dashboard-init (оба каждые 60с).
    let alive = true
    const reload = () => {
      fetch('/api/my-servers')
        .then(r => r.json())
        .then((d: MyServersResponse) => {
          if (!alive) return
          // ВАЖНО (#227): панель-прокси /api/my-servers при сбое связи с
          // лиц-сервером (cold-коннект сразу после кросс-доменного перехода,
          // кратковременный WAN-блип) отдаёт HTTP 200 с телом {"error":...}
          // БЕЗ ключа `servers`. Раньше это превращалось в `[]` →
          // setServers([]) → свитчер скрывался (length<2) до ручного F5.
          // Теперь: нет валидного массива `servers` → это ошибка/частичный
          // ответ, НЕ затираем уже загруженный список (props/предыдущий fetch).
          const list = Array.isArray(d?.servers) ? d.servers : null
          if (!list) return
          setServers(list)
          // Кэшируем naming/url в localStorage, чтобы страница ЛОГИНА
          // (тот же origin, но без сессии) могла показать быстрые ссылки
          // как fallback к публичному endpoint'у. Без секретов.
          try {
            if (list.length > 0) {
              localStorage.setItem('cb-known-servers', JSON.stringify(
                list.map(s => ({
                  subdomain: s.subdomain, domain: s.domain, url: s.url,
                  server_name: s.server_name, server_icon: s.server_icon,
                })),
              ))
            }
          } catch { /* quota / private mode — игнорируем */ }
        })
        .catch(() => {})
        .finally(() => { if (alive) setLoading(false) })
    }
    // Bug #199: ВСЕГДА грузим свежий список при монтировании. Переключение
    // сервера = window.location.href = полная перезагрузка на новый хост и
    // новый mount. Раньше при наличии initialServers (props из dashboard-init)
    // начальный fetch пропускался; если props на момент mount нового хоста
    // были пустыми/устаревшими — дропдаун оставался пустым до ручного F5.
    reload()
    // Обновляем статус серверов каждые 2 минуты (было 60с — дублировало dashboard-init)
    const t = setInterval(reload, 120_000)
    // При фокусе вкладки — тоже обновляем
    const onFocus = () => reload()
    window.addEventListener('focus', onFocus)
    return () => {
      alive = false
      clearInterval(t)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  // Bug #227: `servers` инициализируется из `initialServers` лишь ОДИН раз
  // (useState ignores subsequent prop changes). initialServers приходит из
  // App → dashboard-init АСИНХРОННО (на mount он ещё []). Если собственный
  // fetch /api/my-servers компонента дал сбой сразу после кросс-доменного
  // перехода (cold-коннект → 200 {"error":...} → пустой список), свитчер
  // пропадал до F5. Здесь подхватываем свежий список из props, как только
  // dashboard-init его привёз — независимый запрос обычно успевает, даже
  // когда первый own-fetch свитчера хлебнул ошибку. Берём props только если
  // они содержат БОЛЬШЕ серверов, чем текущее состояние (не затираем
  // собственный, потенциально более свежий, own-fetch).
  useEffect(() => {
    if (Array.isArray(initialServers) && initialServers.length > 0) {
      setServers(prev => (initialServers.length > prev.length ? initialServers : prev))
      setLoading(false)
    }
  }, [initialServers])

  // Sort: current first, then online, then offline; alphabetical inside.
  const sortedServers = useMemo(() => {
    const arr = [...servers]
    arr.sort((a, b) => {
      const aCur = a.domain === currentHost ? 0 : 1
      const bCur = b.domain === currentHost ? 0 : 1
      if (aCur !== bCur) return aCur - bCur
      const aOn = a.is_online ? 0 : 1
      const bOn = b.is_online ? 0 : 1
      if (aOn !== bOn) return aOn - bOn
      return a.domain.localeCompare(b.domain)
    })
    return arr
  }, [servers, currentHost])

  const current = useMemo(
    () => sortedServers.find(s => s.domain === currentHost) || null,
    [sortedServers, currentHost],
  )

  const kindOf = (s: ServerInfo): string => String(s.runtime?.kind || '')
  const tabCounts = useMemo(() => {
    let galaxy = 0, space = 0
    for (const s of sortedServers) {
      const k = kindOf(s)
      if (k === 'galaxy' || k === 'mixed') galaxy++
      if (k === 'space' || k === 'mixed') space++
    }
    return { all: sortedServers.length, galaxy, space }
  }, [sortedServers])
  const showTabs = RICH_SWITCHER && tabCounts.galaxy > 0 && tabCounts.space > 0
  const visibleServers = useMemo(() => {
    if (!showTabs || tab === 'all') return sortedServers
    return sortedServers.filter(s => {
      const k = kindOf(s)
      return k === tab || k === 'mixed'
    })
  }, [sortedServers, tab, showTabs])

  const navigate = useCallback((url: string) => {
    if (!url) return
    // Same-tab nav as the user requested. Hard reload — different
    // origin = different cookies, different session, different panel.
    window.location.href = url
  }, [])

  // Hide entirely when there's nothing to switch between.
  if (loading) return null
  if (sortedServers.length < 2) return null

  // Render label: in collapsed sidebar show only the icon. In expanded
  // sidebar show subdomain (+ optional label) + chevron.
  const CurrentIcon = resolveServerIcon(current?.server_icon)
  const triggerSub = current?.subdomain || current?.domain || currentHost
  const triggerLabel = current?.server_name || ''

  // Visual touch: split a numeric suffix off the subdomain so
  //   "gila1"  → "Gila — 1"
  //   "gila42" → "Gila — 42"
  //   "andy"   → "Andy"          (no digits, single chunk capitalised)
  // The actual `subdomain` value (used for navigation / search) is
  // unchanged — this is purely cosmetic.
  // 11 200 → «11.2к»: в строке шириной с сайдбар полное число съедает
  // место, которое нужнее под остальные показатели.
  const fmtNum = (n: number): string => {
    if (!isFinite(n) || n < 0) return '—'
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.0', '')}к`
    return String(Math.round(n))
  }

  /** Короткая сводка под именем сервера + подробности в подсказке. */
  const serverMetrics = (s: ServerInfo): { text: string; tone: string; title: string } => {
    const rt = s.runtime || {}
    const fresh = !!s.runtime_ts && (Date.now() / 1000 - s.runtime_ts) < 15 * 60
    const parts: string[] = []
    const details: string[] = []
    if (s.ip) details.push(`IP ${s.ip}`)
    if (s.engine_version) details.push(`движок ${s.engine_version}`)

    if (!fresh) {
      // Сервер старой версии (ещё не шлёт сводку) или молчит — не выдумываем
      // цифры, показываем подпись оператора, как было раньше.
      return {
        text: s.server_name || 'нет данных о загрузке',
        tone: s.server_name ? 'text-muted-foreground' : 'text-muted-foreground/50 italic',
        title: details.join(' · '),
      }
    }

    const running = rt.projects_running ?? 0
    const threads = rt.threads_cfg ?? 0
    const alive = rt.workers_alive ?? 0
    const ops = rt.ops_hour ?? -1
    const cpu = s.cpu_percent ?? 0

    if (running === 0) {
      details.push(`проектов ${rt.projects_total ?? 0}, запущено 0`)
      return { text: 'простаивает · проекты не запущены', tone: 'text-amber-500/90', title: details.join(' · ') }
    }

    parts.push(`${alive || threads} пот.`)
    if (ops >= 0) parts.push(`${fmtNum(ops)}/ч`)
    if (cpu > 0) parts.push(`CPU ${Math.round(cpu)}%`)

    details.push(`проектов ${running}/${rt.projects_total ?? running}`)
    details.push(`воркеры ${alive}${threads ? ` из ${threads}` : ''}${rt.workers_active != null ? `, в работе ${rt.workers_active}` : ''}`)
    if (ops >= 0) details.push(`выполнений ${Math.round(ops)}/ч`)
    if ((rt.fail_pct ?? -1) >= 0) details.push(`ошибок ${rt.fail_pct}%`)
    if ((rt.captcha_pct ?? -1) >= 0) details.push(`капча ${rt.captcha_pct}%`)
    if (rt.load1 != null && rt.cores) details.push(`нагрузка ${rt.load1} из ${rt.cores} ядер`)
    if (s.memory_percent) details.push(`память ${Math.round(s.memory_percent)}%`)
    if (rt.idents?.length) details.push(`пулы и проекты: ${rt.idents.slice(0, 5).join(', ')}`)
    if (s.server_name) details.unshift(s.server_name)

    // Подсветка того, на что стоит посмотреть: перегрев по процессору или
    // запущенные проекты без единого выполнения.
    const hot = (rt.load1 != null && rt.cores ? rt.load1 > rt.cores * 1.15 : false) || cpu >= 92
    const stalled = ops === 0
    const tone = stalled ? 'text-amber-500/90' : hot ? 'text-orange-400/90' : 'text-muted-foreground'
    return { text: parts.join(' · '), tone, title: details.join(' · ') }
  }

  const prettySub = (raw: string): string => {
    if (!raw) return ''
    const m = /^([A-Za-zА-Яа-яЁё][A-Za-zА-Яа-яЁё_-]*?)(\d+)$/.exec(raw)
    if (m) {
      const head = m[1]
      const num = m[2]
      return `${head.charAt(0).toUpperCase()}${head.slice(1)} — ${num}`
    }
    return raw.charAt(0).toUpperCase() + raw.slice(1)
  }

  return (
    <div className={`${collapsed ? 'px-1.5' : 'px-2'} pt-2 pb-1`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            className={`w-full flex items-center gap-2 rounded-lg border border-border/60 bg-card/40 hover:bg-accent/50 transition-colors cursor-pointer ${
              collapsed ? 'justify-center h-9 px-0' : 'px-2.5 py-2 text-left'
            }`}
            title={collapsed ? `Сервер: ${prettySub(triggerSub)} · ${sortedServers.length} серверов` : ''}
          >
            <CurrentIcon className="w-4 h-4 shrink-0 text-primary" />
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate leading-tight">
                    {prettySub(triggerSub)}
                  </div>
                  <div
                    className={`text-[10px] truncate leading-tight mt-0.5 ${
                      RICH_SWITCHER && current
                        ? serverMetrics(current).tone
                        : (triggerLabel ? 'text-muted-foreground' : 'text-muted-foreground/50 italic')
                    }`}
                    title={RICH_SWITCHER && current ? serverMetrics(current).title : ''}
                  >
                    {RICH_SWITCHER && current
                      ? serverMetrics(current).text
                      : (triggerLabel || 'без подписи')}
                  </div>
                </div>
                <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              </>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          // Match the popover width to the trigger button so the
          // dropdown lines up flush with the sidebar instead of
          // sticking out into the main area.
          className="p-0"
          style={{ width: triggerWidth ? `${triggerWidth}px` : undefined }}
          align="start"
          sideOffset={4}
        >
          <Command>
            {/* Поиск сразу по имени, домену, подписи и IP — в пуле уже
                несколько десятков серверов, глазами искать долго. */}
            {RICH_SWITCHER && (
              <CommandInput placeholder="Поиск: имя, IP или идентификатор" className="h-9" />
            )}
            {showTabs && (
              <div className="flex items-center gap-1 px-2 pt-1.5 pb-1 border-b border-border/40">
                {([
                  ['all', 'Все', tabCounts.all],
                  ['galaxy', 'Galaxy', tabCounts.galaxy],
                  ['space', 'Space', tabCounts.space],
                ] as const).map(([key, label, count]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    className={`px-2 py-0.5 rounded text-[11px] transition-colors ${
                      tab === key
                        ? 'bg-accent text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label} <span className="text-muted-foreground/60">{count}</span>
                  </button>
                ))}
              </div>
            )}
            <CommandList className="max-h-[420px]">
              <CommandEmpty>Серверы не найдены</CommandEmpty>
              <CommandGroup>
                {visibleServers.map((s) => {
                  const isCurrent = s.domain === currentHost
                  const ItemIcon = resolveServerIcon(s.server_icon)
                  return (
                    <CommandItem
                      key={s.domain}
                      value={`${s.subdomain} ${s.domain} ${s.server_name || ''} ${s.ip || ''} ${(s.runtime?.idents || []).join(' ')}`}
                      onSelect={() => {
                        setOpen(false)
                        if (isCurrent) return
                        navigate(s.url)
                      }}
                      // `cursor-pointer` forced because shadcn's
                      // CommandItem ships with `cursor-default` by
                      // default (accessibility-driven) which makes the
                      // hovered server look unclickable.
                      className="flex items-start gap-2.5 py-2 cursor-pointer"
                    >
                      <div className="relative shrink-0 mt-0.5">
                        <ItemIcon className={`w-4 h-4 ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ring-2 ring-popover ${
                            s.suspended ? 'bg-amber-500' :
                            s.is_online ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                          }`}
                          title={
                            s.suspended ? 'Приостановлен' :
                            s.is_online ? 'Онлайн' : 'Оффлайн'
                          }
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-1 min-w-0">
                          <span className="text-sm font-medium truncate">
                            {prettySub(s.subdomain || s.domain)}
                          </span>
                          <span className="text-[10px] text-muted-foreground/70 truncate">
                            .crp.pro
                          </span>
                        </div>
                        {RICH_SWITCHER ? (() => {
                          const m = serverMetrics(s)
                          return (
                            <div
                              className={`text-[10px] truncate leading-tight ${m.tone}`}
                              title={m.title}
                            >
                              {m.text}
                            </div>
                          )
                        })() : s.server_name ? (
                          <div className="text-[11px] text-muted-foreground truncate leading-tight">
                            {s.server_name}
                          </div>
                        ) : (
                          <div className="text-[10px] text-muted-foreground/50 italic truncate leading-tight">
                            без подписи
                          </div>
                        )}
                      </div>
                      {s.suspended && (
                        <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-amber-500/40 text-amber-500 self-center">
                          Пауза
                        </Badge>
                      )}
                      {isCurrent ? (
                        <Check className="w-4 h-4 text-primary shrink-0 self-center" />
                      ) : (
                        <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0 self-center" />
                      )}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

// Avoid unused-import lint warning if Loader2 happens to be unused.
void Loader2
